import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import { getCompanyName, unwrapPaginated, uploadFile } from "../../config/api";
import { uploadAccept, validateUploadFile } from "../../config/uploads";
import i18n from "../../i18n";
import { formatDate, formatTime } from "../../i18n/format";
import { getApiErrorMessage } from "../../lib/apiError";
import { CONVERSATION_LIST_POLL_MS, conversationQueryKeysToRefresh, THREAD_POLL_MS } from "../../lib/chatQueries";
import { useCompaniesMyCompaniesRetrieve } from "../../services/companies/generated/companies/companies";
import type { Company } from "../../services/companies/generated/model";
import {
  getConversationsListQueryKey,
  useConversationsDetailRetrieve,
  useConversationsList,
  useConversationsMarkReadCreate,
  useConversationsMessagesList,
  useConversationsSendCreate,
} from "../../services/messages/generated/conversations/conversations";
import type { Conversation, Message as ConversationMessage } from "../../services/messages/generated/model";
import "./MessagesPage.css";

function formatTimeAgo(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return i18n.t("b2b:messages.timeAgo.justNow");
    if (diffMins < 60) return i18n.t("b2b:messages.timeAgo.minutes", { count: diffMins });
    if (diffHours < 24) return i18n.t("b2b:messages.timeAgo.hours", { count: diffHours });
    if (diffDays < 7) return i18n.t("b2b:messages.timeAgo.days", { count: diffDays });
    return formatDate(d);
  } catch {
    return iso;
  }
}

function formatMessageTime(iso: string): string {
  try {
    return formatTime(iso, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function getInitials(name: string | number | undefined | null): string {
  const s = name != null ? String(name).trim() : "";
  if (!s) return "??";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  return parts
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** Public moderator shape a company sees on moderator conversations: no email/phone. */
interface ModeratorPublic {
  id?: number;
  fullName?: string | null;
  role?: string | null;
}

/**
 * Fields the list/detail endpoints add on top of the generated `Conversation`
 * (orval types them loosely): moderator conversations carry `moderator` instead
 * of a counterpart company, and company conversations embed the deal scores.
 */
interface ConversationExtras {
  conversationType?: "company" | "moderator";
  isModeratorConversation?: boolean;
  moderator?: ModeratorPublic | null;
  matchResult?: { winWinMatchScore?: number | null } | string | null;
}

type ConversationView = Omit<Conversation, "matchResult"> & ConversationExtras;

/** Moderator-conversation messages use `fromModerator` / `moderatorName` instead of sender company fields. */
type ThreadMessage = Partial<ConversationMessage> & {
  fromModerator?: boolean;
  moderatorName?: string | null;
};

function isModeratorConversation(conv: Conversation): boolean {
  const extras = conv as ConversationView;
  return extras.conversationType === "moderator" || extras.isModeratorConversation === true;
}

function moderatorOf(conv: Conversation): ModeratorPublic | null {
  return (conv as ConversationView).moderator ?? null;
}

/** Win-win score of the deal behind a company conversation, or null when not available. */
function dealScore(conv: Conversation): number | null {
  const mr = (conv as ConversationView).matchResult;
  if (mr == null || typeof mr !== "object") return null;
  return typeof mr.winWinMatchScore === "number" ? mr.winWinMatchScore : null;
}

/** Backend expects composite id: c-{id} for company conv, m-{id} for moderator. B2B Messages uses company conversations. */
function toConversationApiId(id: number | string | null | undefined): string {
  if (id == null) return "";
  const s = String(id).trim();
  if (!s) return "";
  // Already in composite form (from list API)
  if (/^(c|m)-\d+$/.test(s)) return s;
  // Numeric id (e.g. from create response or list)
  return `c-${s}`;
}

const MessagesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["b2b", "common"]);
  /** Display name the backend uses when a moderator has no full name (ModeratorPublicSerializer). */
  const moderatorFallbackName = t("messages.moderatorFallback");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // `openConversationId` is a numeric company-conversation id (Initiate Contact)
  // or a composite `c-<id>` / `m-<id>` (notification click-through). The
  // effect below re-selects whenever it changes, so clicking a notification
  // while already on this page still opens that conversation.
  type MessagesLocationState = { openConversationId?: number | string; isNewConversation?: boolean } | null;
  const openConversationIdFromState = (location.state as MessagesLocationState)?.openConversationId;
  const isNewConversation = (location.state as MessagesLocationState)?.isNewConversation ?? false;

  const [selectedId, setSelectedId] = useState<number | string | null>(() =>
    openConversationIdFromState != null ? toConversationApiId(openConversationIdFromState) : null,
  );
  const [composerValue, setComposerValue] = useState("");
  const conversationItemRef = useRef<Record<string, HTMLButtonElement | null>>({});

  // Upload via the generic /common/upload/ endpoint and append the resulting
  // URL to the composer. Message bodies are plain text on the backend, so we
  // surface the file as a markdown-style link the recipient can click.
  const handleAttachClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    setUploadError(null);
    const invalid = validateUploadFile(file, "document");
    if (invalid) {
      setUploadError(invalid);
      return;
    }
    setIsUploading(true);
    try {
      const { url } = await uploadFile(file);
      setComposerValue((prev) => (prev ? `${prev} ${url}` : url));
    } catch (err) {
      setUploadError(getApiErrorMessage(err, t("messages.errors.upload")));
    } finally {
      setIsUploading(false);
    }
  };

  // Fetch conversations using Orval-generated hook
  // Server-side search over counterpart name (company or moderator) and message content.
  const [conversationSearch, setConversationSearch] = useState("");
  const trimmedConversationSearch = conversationSearch.trim();
  const {
    data: conversationsResponse,
    isLoading: conversationsLoading,
    error: conversationsQueryError,
  } = useConversationsList(trimmedConversationSearch ? { search: trimmedConversationSearch } : undefined, {
    query: { placeholderData: keepPreviousData, refetchInterval: CONVERSATION_LIST_POLL_MS },
  });

  // Extract conversations from paginated response
  // The API returns paginated objects { count, next, previous, results: [] }
  // but Swagger spec incorrectly defines them as plain arrays
  const conversations = unwrapPaginated<Conversation>(conversationsResponse);

  // Format error for display
  const conversationsError = conversationsQueryError ? t("messages.errors.loadConversations") : null;

  // Fetch company data for display name logic
  const { data: companiesData } = useCompaniesMyCompaniesRetrieve();
  const myCompanyName = unwrapPaginated<Company>(companiesData)[0]?.companyName || null;

  const conversationApiId = selectedId != null ? toConversationApiId(selectedId) : "";

  // Fetch conversation detail when selected (only if valid ID exists)
  const { data: detail, isLoading: detailLoading } = useConversationsDetailRetrieve(conversationApiId, {
    query: {
      enabled: selectedId != null && conversationApiId !== "",
    },
  });

  // Fetch messages for selected conversation (only if valid ID exists)
  const { data: messagesResponse, isLoading: messagesQueryLoading } = useConversationsMessagesList(
    conversationApiId,
    undefined,
    {
      query: {
        enabled: selectedId != null && conversationApiId !== "",
        refetchInterval: THREAD_POLL_MS,
      },
    },
  );

  // Extract messages from paginated response
  // The API returns paginated objects { count, next, previous, results: [] }
  // but Swagger spec incorrectly defines them as plain arrays
  const messages = (messagesResponse as any)?.results ?? (Array.isArray(messagesResponse) ? messagesResponse : []);

  const messagesLoading = detailLoading || messagesQueryLoading;

  // Mutation hooks
  const sendMessageMutation = useConversationsSendCreate();
  const markReadMutation = useConversationsMarkReadCreate();

  // When arriving with a new conversation (e.g. from Initiate Contact), refetch list so it appears
  useEffect(() => {
    if (openConversationIdFromState != null) {
      queryClient.invalidateQueries({ queryKey: getConversationsListQueryKey() });
    }
  }, [openConversationIdFromState, queryClient]);

  // Auto-select conversation from state and scroll into view once it appears in the list
  useEffect(() => {
    if (openConversationIdFromState == null) return;
    const apiId = toConversationApiId(openConversationIdFromState);
    setSelectedId(apiId);
    const inList = conversations.some((c: any) => toConversationApiId(c.id) === apiId);
    if (inList) {
      const item = conversationItemRef.current[apiId];
      if (item) {
        setTimeout(() => {
          item.scrollIntoView({ behavior: "smooth", block: "nearest" });
          item.focus();
        }, 100);
      }
    }
  }, [openConversationIdFromState, conversations]);

  const refreshConversation = (apiId: string) => {
    for (const queryKey of conversationQueryKeysToRefresh(apiId)) {
      queryClient.invalidateQueries({ queryKey });
    }
  };

  // Mark the open conversation as read when it is selected and whenever a
  // poll brings in a new message, then refresh the unread badges.
  const newestMessageId = messages.length ? messages[messages.length - 1]?.id : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when a poll brings a new message (newestMessageId); refreshConversation is recreated every render.
  useEffect(() => {
    if (selectedId != null && detail && conversationApiId) {
      markReadMutation.mutate(
        { id: conversationApiId },
        {
          onSuccess: (res) => {
            if (Number((res as { updatedCount?: number } | undefined)?.updatedCount) > 0) {
              refreshConversation(conversationApiId);
            }
          },
        },
      );
    }
  }, [selectedId, detail, conversationApiId, newestMessageId, markReadMutation.mutate]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = composerValue.trim();
    if (!content || selectedId == null || sendMessageMutation.isPending) return;

    sendMessageMutation.mutate(
      { id: conversationApiId, data: { content } },
      {
        onSuccess: () => {
          refreshConversation(conversationApiId);
          setComposerValue("");
        },
        onError: () => {
          // Shown by the global mutation toast (src/lib/queryClient.ts).
        },
      },
    );
  };

  const displayName = (conv: Conversation): string => {
    if (isModeratorConversation(conv)) {
      return moderatorOf(conv)?.fullName?.trim() || moderatorFallbackName;
    }
    const nameA = getCompanyName(conv.companyA);
    const nameB = getCompanyName(conv.companyB);
    if (myCompanyName && nameA === myCompanyName) return nameB;
    if (myCompanyName && nameB === myCompanyName) return nameA;
    return `${nameA} · ${nameB}`;
  };

  const detailIsModerator = detail ? isModeratorConversation(detail) : false;
  const detailScore = detail && !detailIsModerator ? dealScore(detail) : null;

  // In a moderator conversation every non-moderator message is from our company.
  const isOwnMessage = (msg: ThreadMessage) => {
    if (detailIsModerator) return msg.fromModerator !== true;
    return myCompanyName ? msg.senderCompanyName === myCompanyName : false;
  };

  const partnerSenderLabel = (msg: ThreadMessage): string => {
    if (detailIsModerator) return msg.moderatorName?.trim() || moderatorFallbackName;
    return String(msg.senderCompanyName ?? "");
  };

  return (
    <DashboardLayout showSearch={false}>
      <div className="messages">
        <header className="dashboard__section-header">
          <div>
            <h1 className="dashboard__title">{t("messages.title")}</h1>
            <p className="dashboard__subtitle">{t("messages.subtitle")}</p>
          </div>
        </header>

        <div className="messages__layout">
          <section className="messages__list-panel" aria-label={t("messages.listAria")}>
            <div className="messages__list-header">
              <div className="messages__list-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input
                  type="search"
                  placeholder={t("messages.searchPlaceholder")}
                  aria-label={t("messages.searchAria")}
                  value={conversationSearch}
                  onChange={(e) => setConversationSearch(e.target.value)}
                />
              </div>
            </div>

            {conversationsError && (
              <div className="messages__list-error" role="alert">
                {conversationsError}
              </div>
            )}

            {conversationsLoading ? (
              <div className="messages__list-loading">
                <i className="fa-solid fa-spinner fa-spin" />
                <span>{t("common:states.loading")}</span>
              </div>
            ) : (
              <div className="messages__list">
                {conversations.length === 0 ? (
                  <p className="messages__list-empty">
                    {trimmedConversationSearch ? t("messages.empty.noMatches") : t("messages.empty.noConversations")}
                  </p>
                ) : (
                  conversations.map((conv: any) => {
                    const convApiId = toConversationApiId(conv.id);
                    return (
                      <button
                        ref={(el) => {
                          if (el) conversationItemRef.current[convApiId] = el;
                          else delete conversationItemRef.current[convApiId];
                        }}
                        key={convApiId}
                        type="button"
                        className={`messages__list-item ${
                          convApiId === conversationApiId ? "messages__list-item--active" : ""
                        } ${convApiId === conversationApiId && isNewConversation ? "messages__list-item--new" : ""}`}
                        onClick={() => setSelectedId(convApiId)}
                      >
                        <div className="messages__list-item-main">
                          <div className="messages__avatar">
                            <span>{getInitials(displayName(conv))}</span>
                          </div>
                          <div className="messages__list-text">
                            <div className="messages__list-row">
                              <span className="messages__company-name">{displayName(conv)}</span>
                            </div>
                            <span className="messages__time-ago">
                              {conv.lastMessageAt ? formatTimeAgo(conv.lastMessageAt) : "—"}
                            </span>
                            <p className="messages__preview">
                              {typeof conv.lastMessage === "string"
                                ? conv.lastMessage
                                : ((conv.lastMessage as any)?.content ?? t("messages.noMessagesPreview"))}
                            </p>
                          </div>
                        </div>
                        <div className="messages__list-meta">
                          {Number(conv.unreadCount) > 0 && (
                            <span className="messages__unread-badge">{conv.unreadCount}</span>
                          )}
                          {convApiId === conversationApiId && isNewConversation && (
                            <span className="messages__new-badge" title={t("messages.newConversation")}>
                              <i className="fa-solid fa-star" />
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </section>

          <section className="messages__detail-panel" aria-label={t("messages.detailAria")}>
            {selectedId == null ? (
              <div className="messages__detail-empty">
                <i className="fa-regular fa-comment-dots" />
                <p>{t("messages.selectPrompt")}</p>
              </div>
            ) : messagesLoading && messages.length === 0 ? (
              <div className="messages__detail-loading">
                <i className="fa-solid fa-spinner fa-spin" />
                <span>{t("messages.loadingConversation")}</span>
              </div>
            ) : detail ? (
              <>
                {!detailIsModerator && (
                  <article className="messages__related-card">
                    <div className="messages__related-top">
                      <div className="messages__related-main">
                        <div className="messages__related-icon">
                          <i className="fa-solid fa-chart-line" aria-hidden />
                        </div>
                        <div className="messages__related-text">
                          <span className="messages__related-label">{t("messages.relatedMatch")}</span>
                          <h2 className="messages__related-title">{displayName(detail)}</h2>
                          <p className="messages__related-meta">
                            <i className="fa-solid fa-building" aria-hidden />
                            {getCompanyName(detail.companyA)} · {getCompanyName(detail.companyB)}
                          </p>
                        </div>
                      </div>
                      <div className="messages__related-right">
                        {detailScore != null && <span className="messages__related-score-pill">{detailScore}%</span>}
                        <button
                          type="button"
                          className="messages__related-open-button"
                          aria-label={t("messages.openMatchAria")}
                          onClick={() => navigate("/b2b/match-results")}
                        >
                          <i className="fa-solid fa-square-arrow-up-right" aria-hidden />
                        </button>
                      </div>
                    </div>
                  </article>
                )}

                <div className="messages__thread">
                  <div className="messages__thread-header">
                    <div className="messages__thread-avatar">
                      <span>{getInitials(displayName(detail))}</span>
                    </div>
                    <div>
                      <p className="messages__thread-company">{displayName(detail)}</p>
                      {/* No presence data on the backend: show the role and the
                          conversation's last activity instead of a fake "Active now". */}
                      {(detailIsModerator && moderatorOf(detail)?.role) || detail.lastMessageAt ? (
                        <p className="messages__thread-status">
                          {[
                            detailIsModerator ? moderatorOf(detail)?.role : null,
                            detail.lastMessageAt
                              ? t("messages.lastMessage", { time: formatTimeAgo(detail.lastMessageAt) })
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="messages__thread-body">
                    {messages.length === 0 ? (
                      <p className="messages__thread-empty">{t("messages.threadEmpty")}</p>
                    ) : (
                      messages.map((msg: any) => {
                        const own = isOwnMessage(msg);
                        return (
                          <div
                            key={msg.id}
                            className={`messages__bubble-row messages__bubble-row--${own ? "user" : "partner"}`}
                          >
                            {!own && (
                              <div className="messages__bubble-wrap messages__bubble-wrap--partner">
                                <div className="messages__bubble-avatar messages__bubble-avatar--partner">
                                  <span>{getInitials(partnerSenderLabel(msg))}</span>
                                </div>
                                <div className="messages__bubble-block">
                                  <div className={`messages__bubble messages__bubble--partner`}>
                                    {String(msg.content)}
                                  </div>
                                  <span className="messages__bubble-time">
                                    {(detailIsModerator
                                      ? [partnerSenderLabel(msg)]
                                      : [msg.senderName, msg.senderCompanyName].filter(Boolean)
                                    ).join(" · ")}
                                    {detailIsModerator || msg.senderName || msg.senderCompanyName ? " · " : ""}
                                    {formatMessageTime(String(msg.createdAt))}
                                  </span>
                                </div>
                              </div>
                            )}
                            {own && (
                              <div className="messages__bubble-wrap messages__bubble-wrap--user">
                                <div className="messages__bubble-block">
                                  <div className="messages__bubble messages__bubble--user">{String(msg.content)}</div>
                                  <span className="messages__bubble-time">
                                    {formatMessageTime(String(msg.createdAt))}
                                  </span>
                                </div>
                                <div className="messages__bubble-avatar messages__bubble-avatar--user">
                                  <span>{getInitials(myCompanyName ?? "U")}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {uploadError ? (
                    <p className="messages__upload-error" role="alert">
                      {uploadError}
                    </p>
                  ) : null}
                  <form className="messages__composer" onSubmit={handleSend}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={uploadAccept("document")}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                    <button
                      type="button"
                      className="messages__composer-attach"
                      aria-label={t("messages.attachFile")}
                      onClick={handleAttachClick}
                      disabled={isUploading}
                      title={isUploading ? t("common:actions.uploading") : t("messages.attachFile")}
                    >
                      <i className={`fa-solid ${isUploading ? "fa-spinner fa-spin" : "fa-paperclip"}`} aria-hidden />
                    </button>
                    <input
                      type="text"
                      className="messages__composer-input"
                      placeholder={t("messages.composerPlaceholder")}
                      value={composerValue}
                      onChange={(e) => setComposerValue(e.target.value)}
                      disabled={sendMessageMutation.isPending}
                    />
                    <button
                      type="submit"
                      className="messages__composer-send"
                      disabled={sendMessageMutation.isPending || !composerValue.trim()}
                      aria-label={t("messages.sendAria")}
                    >
                      {sendMessageMutation.isPending ? (
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane" aria-hidden />
                          <span>{t("common:actions.send")}</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </>
            ) : null}
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MessagesPage;
